import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Play, Square, Maximize2, Minimize2, Settings, X } from 'lucide-react';
import CaptureLivePreview from './CaptureLivePreview';

interface PopupRecordingFrameProps {
  isOpen: boolean;
  onClose: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  isRecording: boolean;
  previewStream?: MediaStream | null;
  captureWarning?: string | null;
}

const PopupRecordingFrame: React.FC<PopupRecordingFrameProps> = ({
  isOpen,
  onClose,
  onStartRecording,
  onStopRecording,
  isRecording,
  previewStream = null,
  captureWarning = null,
}) => {
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [fileSize, setFileSize] = useState(0);

  const frameRef = useRef<HTMLDivElement>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isRecording) {
      durationIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
        setFileSize((prev) => prev + 0.017);
      }, 1000);
    } else if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [isRecording]);

  const formatDuration = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (mb: number) => {
    if (mb < 1) {
      return `${(mb * 1024).toFixed(0)} KB`;
    }
    return `${mb.toFixed(1)} MB`;
  };

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      frameRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const handleRecordingToggle = useCallback(() => {
    if (isRecording) {
      onStopRecording();
    } else {
      onStartRecording();
    }
  }, [isRecording, onStartRecording, onStopRecording]);

  const handleResize = useCallback(
    (e: React.MouseEvent) => {
      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = dimensions.width;
      const startHeight = dimensions.height;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const newWidth = Math.max(400, Math.min(1920, startWidth + (moveEvent.clientX - startX)));
        const newHeight = Math.max(300, Math.min(1080, startHeight + (moveEvent.clientY - startY)));
        setDimensions({ width: newWidth, height: newHeight });
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [dimensions]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={frameRef}
        className="bg-white rounded-lg shadow-2xl overflow-hidden"
        style={{ width: dimensions.width, height: dimensions.height }}
      >
        <div className="bg-gray-800 text-white p-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleRecordingToggle}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                isRecording
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {isRecording ? (
                <>
                  <Square className="h-4 w-4" />
                  <span>Stop Recording</span>
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  <span>Start Recording</span>
                </>
              )}
            </button>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-300">Resolution:</span>
              <span className="text-sm font-mono bg-gray-700 px-2 py-1 rounded">
                {dimensions.width} × {dimensions.height}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              title="Settings"
            >
              <Settings className="h-4 w-4" />
            </button>

            <button
              onClick={toggleFullscreen}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>

            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {showSettings && (
          <div className="bg-gray-700 text-white p-4 border-t border-gray-600">
            <h3 className="text-sm font-medium mb-3">SmartPSS / camera tips</h3>
            <p className="text-xs text-gray-300">
              Share the entire monitor (not the SmartPSS window). In SmartPSS open System Config and disable hardware
              acceleration. Use the 32-bit SmartPSS client if the live feed stays black.
            </p>
          </div>
        )}

        <div
          className="bg-gray-900 flex items-center justify-center relative overflow-hidden"
          style={{ height: `calc(100% - ${showSettings ? '160px' : '120px'})` }}
        >
          {isRecording && (
            <div className="absolute top-4 left-4 flex items-center space-x-2 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium z-10">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <span>REC</span>
            </div>
          )}

          <CaptureLivePreview
            stream={previewStream}
            warning={captureWarning}
            className="w-full h-full"
          />

          <div
            className="absolute bottom-0 right-0 w-4 h-4 bg-blue-500 cursor-se-resize"
            onMouseDown={handleResize}
            style={{
              background:
                'linear-gradient(-45deg, transparent 30%, #3b82f6 30%, #3b82f6 70%, transparent 70%)',
            }}
          />
        </div>

        <div className="bg-gray-100 p-3 flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <span className={`font-medium ${isRecording ? 'text-red-600' : 'text-gray-600'}`}>
              Status: {isRecording ? 'Recording' : 'Ready'}
            </span>
            <span className="text-gray-500">Duration: {formatDuration(recordingDuration)}</span>
          </div>
          <div className="text-gray-500">File Size: {formatFileSize(fileSize)}</div>
        </div>
      </div>
    </div>
  );
};

export default PopupRecordingFrame;
